import type { Project } from "@/features/projects/types";
import { addDays, endOfDay, startOfDay } from "date-fns";
import { type DataTableConfig, dataTableConfigExperimental } from "../config";
import type {
	ColumnType,
	FilterCondition,
	FilterOperator,
	JoinOperator,
} from "../types";

export function filterColumn({
	column,
	value,
	isSelectable,
}: {
	column: string; // Usar el nombre de la columna como una cadena
	value: string;
	isSelectable?: boolean;
}) {
	const [filterValue, filterOperator] = (value?.split("~").filter(Boolean) ??
		[]) as [
			string,
			DataTableConfig["comparisonOperators"][number]["value"] | undefined,
		];

	if (!filterValue && !["isNull", "isNotNull"].includes(filterOperator ?? ""))
		return "";

	if (isSelectable) {
		switch (filterOperator) {
			case "eq":
				return `p.${column} IN (${filterValue
					?.split(".")
					.map((val) => `'${val}'`)
					.join(", ")})`;
			case "notEq":
				return `p.${column} NOT IN (${filterValue
					?.split(".")
					.map((val) => `'${val}'`)
					.join(", ")})`;
			case "isNull":
				return `p.${column} IS NULL`;
			case "isNotNull":
				return `p.${column} IS NOT NULL`;
			default:
				return `p.${column} IN (${filterValue
					?.split(".")
					.map((val) => `'${val}'`)
					.join(", ")})`;
		}
	}

	switch (filterOperator) {
		case "ilike":
			return `p.${column} LIKE '%${filterValue}%'`;
		case "notIlike":
			return `p.${column} NOT LIKE '%${filterValue}%'`;
		case "startsWith":
			return `p.${column} LIKE '${filterValue}%'`;
		case "endsWith":
			return `p.${column} LIKE '%${filterValue}'`;
		case "eq":
			return `p.${column} = '${filterValue}'`;
		case "notEq":
			return `p.${column} != '${filterValue}'`;
		case "isNull":
			return `p.${column} IS NULL`;
		case "isNotNull":
			return `p.${column} IS NOT NULL`;
		default:
			return `p.${column} LIKE '%${filterValue}%'`;
	}
}

// type ExtendedProject = Project & {
// 	id_user_team: string; // Aquí se agrega `user_team` de manera opcional.
// };
/**
 * Construye condiciones SQL basadas en los filtros proporcionados para una tabla específica.
 *
 * Esta función toma una tabla y un array de condiciones de filtro, y devuelve una expresión
 * SQL que representa la combinación lógica de estas condiciones.
 *
 * Las condiciones se combinan usando el operador lógico especificado ('AND' o 'OR'), determinado
 * por la propiedad joinOperator del primer filtro.
 *
 * Cada condición de filtro puede especificar varios operadores (ej., igualdad, desigualdad,
 * comparación de números y fechas) y la función generará las expresiones SQL apropiadas.
 *
 * @param column - El nombre de la columna, a la que se aplican los filtros.
 * @param filters - Un array de condiciones de filtro a aplicar.
 * @param joinOperator - El operador de unión para combinar las condiciones.
 * @returns Una cadena SQL representando las condiciones combinadas, o una cadena vacía si no se encontraron condiciones válidas.
 */
export function filterColumnsExperimental({
	filters,
	joinOperator,
}: {
	filters: FilterCondition<Project>[];
	joinOperator: JoinOperator;
}): string {
	const joinFn = joinOperator === "and" ? "AND" : "OR";

	const conditions = filters.map((filter) => {
		const column = filter.id;
		console.log(filter)
		switch (filter.operator) {
			case "eq":
				if (Array.isArray(filter.value)) {
					return `p.${column} IN (${filter.value
						.map((val) => `'${val}'`)
						.join(", ")})`;
				}
				if (filter.type === "boolean" && typeof filter.value === "string") {
					return `p.${column} = ${filter.value === "true" ? 1 : 0}`;
				}
				if (filter.type === "date") {
					const date = new Date(filter.value);
					const start = startOfDay(date).toISOString();
					const end = endOfDay(date).toISOString();
					return `(p.${column} >= '${start}' AND p.${column} <= '${end}')`;
				}
				return `p.${column} = '${filter.value}'`;
			case "ne":
				if (Array.isArray(filter.value)) {
					return `p.${column} NOT IN (${filter.value
						.map((val) => `'${val}'`)
						.join(", ")})`;
				}
				if (filter.type === "boolean") {
					return `p.${column} != ${filter.value === "true" ? 1 : 0}`;
				}
				if (filter.type === "date") {
					const date = new Date(filter.value);
					const start = startOfDay(date).toISOString();
					const end = endOfDay(date).toISOString();
					return `(p.${column} < '${start}' OR p.${column} > '${end}')`;
				}
				return `p.${column} != '${filter.value}'`;
			case "iLike":
				return filter.type === "text" && typeof filter.value === "string"
					? `p.${column} LIKE '%${filter.value}%'`
					: "";
			case "notILike":
				return filter.type === "text" && typeof filter.value === "string"
					? `p.${column} NOT LIKE '%${filter.value}%'`
					: "";
			case "lt":
				return (filter.type === "number" || filter.type === "date") &&
					typeof filter.value === "string"
					? `p.${column} < '${filter.value}'`
					: "";
			case "lte":
				return (filter.type === "number" || filter.type === "date") &&
					typeof filter.value === "string"
					? `p.${column} <= '${filter.value}'`
					: "";
			case "gt":
				return (filter.type === "number" || filter.type === "date") &&
					typeof filter.value === "string"
					? `p.${column} > '${filter.value}'`
					: "";
			case "gte":
				return (filter.type === "number" || filter.type === "date") &&
					typeof filter.value === "string"
					? `p.${column} >= '${filter.value}'`
					: "";
			case "isEmpty":
				return `(p.${column} IS NULL OR p.${column} = '')`;
			case "isNotEmpty":
				return `(p.${column} IS NOT NULL AND p.${column} != '')`;
			case "isBefore":
				return filter.type === "date" && typeof filter.value === "string"
					? `p.${column} < '${endOfDay(new Date(filter.value)).toISOString()}'`
					: "";
			case "isAfter":
				return filter.type === "date" && typeof filter.value === "string"
					? `p.${column} > '${startOfDay(
						new Date(filter.value),
					).toISOString()}'`
					: "";
			case "isOnOrBefore":
				return filter.type === "date" && typeof filter.value === "string"
					? `p.${column} <= '${endOfDay(new Date(filter.value)).toISOString()}'`
					: "";
			case "isOnOrAfter":
				return filter.type === "date" && typeof filter.value === "string"
					? `p.${column} >= '${startOfDay(
						new Date(filter.value),
					).toISOString()}'`
					: "";
			case "isBetween":
				if (
					filter.type === "date" &&
					Array.isArray(filter.value) &&
					filter.value.length === 2
				) {
					const [start, end] = filter.value;
					return `(p.${column} >= '${startOfDay(
						new Date(start),
					).toISOString()}' AND p.${column} <= '${endOfDay(
						new Date(end),
					).toISOString()}')`;
				}
				return "";
			case "isRelativeToToday":
				if (filter.type === "date" && typeof filter.value === "string") {
					const today = new Date();
					const [amount, unit] = filter.value.split(" ") ?? [];
					let startDate: Date;
					let endDate: Date;

					if (!amount || !unit) return "";

					switch (unit) {
						case "days":
							startDate = startOfDay(addDays(today, Number.parseInt(amount)));
							endDate = endOfDay(startDate);
							break;
						case "weeks":
							startDate = startOfDay(
								addDays(today, Number.parseInt(amount) * 7),
							);
							endDate = endOfDay(addDays(startDate, 6));
							break;
						case "months":
							startDate = startOfDay(
								addDays(today, Number.parseInt(amount) * 30),
							);
							endDate = endOfDay(addDays(startDate, 29));
							break;
						default:
							return "";
					}

					return `(p.${column} >= '${startDate.toISOString()}' AND p.${column} <= '${endDate.toISOString()}')`;
				}
				return "";
			default:
				throw new Error(`Unsupported operator: ${filter.operator}`);
		}
	});

	const validConditions = conditions.filter((condition) => condition !== "");

	return validConditions.length > 0 ? validConditions.join(` ${joinFn} `) : "";
}

/**
 * Determine the default filter operator for a given column type.
 *
 * This function returns the most appropriate default filter operator based on the
 * column's data type. For text columns, it returns 'iLike' (case-insensitive like),
 * while for all other types, it returns 'eq' (equality).
 *
 * @param columnType - The type of the column (e.g., 'text', 'number', 'date', etc.).
 * @returns The default FilterOperator for the given column type.
 */
export function getDefaultFilterOperator(
	columnType: ColumnType,
): FilterOperator {
	if (columnType === "text") {
		return "iLike";
	}

	return "eq";
}

/**
 * Retrieve the list of applicable filter operators for a given column type.
 *
 * This function returns an array of filter operators that are relevant and applicable
 * to the specified column type. It uses a predefined mapping of column types to
 * operator lists, falling back to text operators if an unknown column type is provided.
 *
 * @param columnType - The type of the column for which to get filter operators.
 * @returns An array of objects, each containing a label and value for a filter operator.
 */
export function getFilterOperators(columnType: ColumnType) {
	const operatorMap: Record<
		ColumnType,
		{ label: string; value: FilterOperator }[]
	> = {
		text: dataTableConfigExperimental.textOperators,
		number: dataTableConfigExperimental.numericOperators,
		select: dataTableConfigExperimental.selectOperators,
		"multi-select": dataTableConfigExperimental.selectOperators,
		boolean: dataTableConfigExperimental.booleanOperators,
		date: dataTableConfigExperimental.dateOperators,
	};

	return operatorMap[columnType] ?? dataTableConfigExperimental.textOperators;
}
